import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Seo, { ldBreadcrumb, ldLocalBusiness } from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { apiClient } from "@/lib/api";
import { ShieldCheck, MapPin, Star, ArrowRight, BookOpen, Compass } from "lucide-react";

const PROFESSION_INTRO = {
  "avocat": "Avocats référencés CONFILEX en droit des affaires, droit social, droit immobilier, droit fiscal, contentieux et structuration de société. Sélection institutionnelle, profils vérifiés, mise en relation confidentielle.",
  "expert-comptable": "Experts-comptables référencés CONFILEX pour la création d'entreprise, la comptabilité courante, l'optimisation fiscale, l'audit et la préparation de cession. Réseau sélectif, accompagnement structuré.",
  "notaire": "Notaires référencés CONFILEX pour les actes authentiques, la transmission patrimoniale, les régimes matrimoniaux, les opérations immobilières et la stratégie patrimoniale familiale ou d'entreprise.",
};

const PROFESSION_LABELS = {
  "avocat": "Avocat",
  "expert-comptable": "Expert-comptable",
  "notaire": "Notaire",
};

export default function SpecialistesProfession() {
  const { profession, city: cityParam } = useParams();
  const cityHuman = cityParam ? cityParam.replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase()) : null;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true); setData(null);
    const params = {};
    if (cityHuman) params.city = cityHuman;
    apiClient.get(`/specialistes/${profession}`, { params })
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  }, [profession, cityHuman]);

  const label = data?.label || PROFESSION_LABELS[profession] || profession;
  const intro = PROFESSION_INTRO[profession] || `Spécialistes ${label.toLowerCase()} référencés par CONFILEX.`;
  const pageTitle = cityHuman
    ? `${label} à ${cityHuman} — Spécialistes référencés`
    : `${label} — Réseau de spécialistes référencés`;
  const description = cityHuman
    ? `Sélection d'${label.toLowerCase()}s référencés par CONFILEX à ${cityHuman}. Profils vérifiés, mise en relation institutionnelle, orientation guidée.`
    : intro;

  const breadcrumbItems = [
    { name: "Accueil", url: "/" },
    { name: "Spécialistes", url: "/experts" },
    { name: label, url: `/specialistes/${profession}` },
  ];
  if (cityHuman) breadcrumbItems.push({ name: cityHuman, url: `/specialistes/${profession}/${cityParam}` });

  const jsonLd = [
    ldLocalBusiness(profession, label, cityHuman),
    ldBreadcrumb(breadcrumbItems),
  ];

  return (
    <div className="bg-[#FAFAF7] min-h-screen">
      <Seo
        title={pageTitle}
        description={description}
        canonical={typeof window !== "undefined" ? window.location.href : undefined}
        jsonLd={jsonLd}
      />
      <Navbar variant="light" />

      {/* Hero */}
      <section className="bg-[#0B3A2A] text-white relative overflow-hidden">
        <div className="absolute -top-32 -left-32 w-[420px] h-[420px] bg-[#C9A961]/10 rounded-full blur-3xl"></div>
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-14 lg:py-20 relative">
          <nav className="text-xs text-white/55 mb-5" aria-label="Fil d'Ariane">
            <Link to="/" className="hover:text-[#C9A961]">Accueil</Link>
            <span className="mx-2">/</span>
            <Link to="/experts" className="hover:text-[#C9A961]">Spécialistes</Link>
            <span className="mx-2">/</span>
            <span className="text-white/80">{label}</span>
            {cityHuman && (<><span className="mx-2">/</span><span className="text-white/80">{cityHuman}</span></>)}
          </nav>
          <Badge className="bg-[#C9A961]/10 text-[#C9A961] border border-[#C9A961]/30 rounded-full mb-5">
            Réseau référencé CONFILEX
          </Badge>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-[56px] tracking-tight leading-[1.05] mb-5 max-w-3xl">
            {cityHuman
              ? <>{label} à <span className="italic text-[#C9A961]">{cityHuman}</span></>
              : <>Réseau d'<span className="italic text-[#C9A961]">{label.toLowerCase()}s</span> référencés</>}
          </h1>
          <p className="text-base md:text-lg text-white/70 max-w-2xl leading-relaxed mb-8">{intro}</p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button asChild className="rounded-full bg-[#C9A961] hover:bg-[#E3C77A] text-[#0B3A2A] font-semibold px-7 h-12">
              <Link to={`/orientation`}>Démarrer l'orientation guidée</Link>
            </Button>
            <Button asChild variant="outline" className="rounded-full border-white/20 bg-transparent text-white hover:bg-white/10 px-7 h-12">
              <Link to={`/orientation-intelligente?q=${encodeURIComponent(label)}`}>Décrire ma situation</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Spécialistes */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-14 lg:py-20">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#C9A961] mb-2">Profils référencés</div>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight text-[#0B3A2A]">
              {cityHuman ? `${label}s à ${cityHuman}` : `Sélection ${label.toLowerCase()}`}
            </h2>
          </div>
          {data?.experts?.length > 0 && (
            <Link to="/orientation-intelligente" className="text-sm text-[#C9A961] hover:underline flex items-center gap-1">
              Décrire ma situation <ArrowRight className="w-4 h-4" />
            </Link>
          )}
        </div>

        {loading && <div className="text-slate-500">Chargement…</div>}
        {!loading && (data?.experts?.length === 0) && (
          <div className="bg-white rounded-2xl border border-slate-100 p-10 text-center">
            <p className="text-slate-600 mb-5">
              Aucun profil {label.toLowerCase()} {cityHuman ? `n'est référencé à ${cityHuman}` : "ne correspond"} pour le moment.
            </p>
            <Button asChild variant="outline" className="rounded-full">
              <Link to="/orientation-intelligente">Décrire ma situation</Link>
            </Button>
          </div>
        )}

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {data?.experts?.map(e => (
            <Link key={e.id} to={`/experts/${e.id}`} data-testid={`landing-expert-${e.id}`}
              className="group bg-white rounded-2xl border border-slate-100 overflow-hidden hover:border-[#C9A961]/40 hover:shadow-md transition-smooth">
              {e.photo && (
                <div className="aspect-[4/3] overflow-hidden bg-slate-100">
                  <img src={e.photo} alt="" className="w-full h-full object-cover group-hover:scale-[1.03] transition-smooth" />
                </div>
              )}
              <div className="p-5">
                <div className="flex items-center gap-2 mb-1.5">
                  <h3 className="font-serif text-lg text-[#0B3A2A]">{e.first_name} {e.last_name}</h3>
                  {e.verified && <ShieldCheck className="w-4 h-4 text-[#C9A961]" />}
                </div>
                <p className="text-sm text-slate-600 mb-3">{e.profession}</p>
                <div className="flex items-center gap-3 text-xs text-slate-500">
                  {e.city && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{e.city}</span>}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Local SEO cluster — other cities for this profession */}
      {!cityHuman && data?.cities?.length > 0 && (
        <section className="bg-white border-y border-slate-100">
          <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12">
            <div className="text-xs uppercase tracking-[0.2em] text-[#C9A961] mb-3">Implantation</div>
            <h2 className="font-serif text-2xl md:text-3xl tracking-tight text-[#0B3A2A] mb-6">
              {label}s par ville
            </h2>
            <div className="flex flex-wrap gap-2">
              {data.cities.map(c => (
                <Link
                  key={c}
                  to={`/specialistes/${profession}/${c.toLowerCase().replace(/\s+/g, "-")}`}
                  data-testid={`city-link-${c}`}
                  className="px-4 py-2 rounded-full border border-slate-200 text-sm text-slate-700 hover:border-[#C9A961]/40 hover:text-[#0B3A2A] transition-smooth bg-white"
                >
                  {label}s à {c}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Articles */}
      {data?.articles?.length > 0 && (
        <section className="max-w-6xl mx-auto px-6 lg:px-10 py-14">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#C9A961] mb-2 flex items-center gap-1.5">
                <BookOpen className="w-3.5 h-3.5" /> Cahier éditorial
              </div>
              <h2 className="font-serif text-3xl md:text-4xl tracking-tight text-[#0B3A2A]">
                Analyses du domaine {label.toLowerCase()}
              </h2>
            </div>
            <Link to={`/insights?profession=${profession}`} className="text-sm text-[#C9A961] hover:underline flex items-center gap-1">
              Toutes les analyses <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {data.articles.slice(0, 3).map(a => (
              <Link key={a.id} to={`/insights/${a.slug}`} data-testid={`landing-article-${a.slug}`}
                className="group bg-white rounded-2xl border border-slate-100 overflow-hidden hover:border-[#C9A961]/40 transition-smooth">
                {a.cover_image && (
                  <div className="aspect-[16/10] overflow-hidden bg-slate-100">
                    <img src={a.cover_image} alt="" className="w-full h-full object-cover group-hover:scale-[1.03] transition-smooth" />
                  </div>
                )}
                <div className="p-5">
                  <h3 className="font-serif text-lg text-[#0B3A2A] leading-snug mb-2 line-clamp-2">{a.title}</h3>
                  <p className="text-sm text-slate-600 line-clamp-2">{a.excerpt}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 pb-16">
        <div className="bg-gradient-to-br from-[#0B3A2A] to-[#26334F] text-white rounded-3xl p-10 lg:p-14 text-center">
          <Compass className="w-7 h-7 text-[#C9A961] mx-auto mb-4" />
          <h3 className="font-serif text-3xl md:text-4xl tracking-tight mb-4">
            Pas certain du bon profil ?
          </h3>
          <p className="text-white/70 mb-7 max-w-xl mx-auto">
            Quelques questions ciblées suffisent. Notre dispositif d'orientation identifie les profils les plus alignés avec votre dossier.
          </p>
          <Button asChild className="rounded-full bg-[#C9A961] hover:bg-[#E3C77A] text-[#0B3A2A] font-semibold px-7 h-12">
            <Link to="/orientation">Démarrer l'orientation</Link>
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
